import { ROLE } from "~/assets/js/constants";
import { isAllowedTo, orderMapper } from "~/assets/js/utils";
import { toAgency } from "~/transformers/agencyTransformer";
import { toSector } from "~/transformers/sectorTransformer";

export default {
  async fetch({ dispatch, commit, rootGetters }, args = {}) {
    commit("SET_LOADING", true);
    try {
      const { isFederatedView = false, instanceId = rootGetters["application/getMainInstanceId"] } =
        args;

      commit("SET_INSTANCE_ID", instanceId);
      commit("RESET_OPEN_AGENCIES");
      dispatch("application/setFederatedView", isFederatedView, { root: true });

      await Promise.all([
        dispatch("setSectors"),
        dispatch("setSectorList"),
        dispatch("updateAgenciesList"),
      ]);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async setSectors({ state, commit, rootGetters }, args = {}) {
    const { sortBy, orderBy, page, filter } = args;
    const { instanceId } = state;
    const size = rootGetters["application/getPageSize"];

    try {
      let sectors;
      if (filter) {
        sectors = await this.$services.agency.searchSectors(instanceId, {
          size,
          page,
          sortBy,
          orderBy,
          filter,
        });
        commit("SET_SECTORS", sectors);
        for (const sector of sectors.collection) {
          commit("SET_AGENCIES", { parent: sector.id, agencies: sector.agencies });
        }
      } else {
        commit("RESET_OPEN_AGENCIES");
        sectors = await this.$services.agency.fetchSectors(instanceId, {
          size,
          page,
          sortBy,
          orderBy,
        });
        commit("SET_SECTORS", sectors);
      }
    } catch {
      this.$feedback.error("generic.error");
    }
  },

  async setSectorList({ state, commit }, args = {}) {
    const instanceId = args.instanceId ?? state.instanceId;
    try {
      const sectors = await this.$services.agency.fetchSectors(instanceId, {
        size: 10000,
        page: 0,
        sortBy: "name",
        orderBy: "asc",
      });
      commit("SET_SECTOR_LIST", sectors);
    } catch (error) {
      throw new Error(error);
    }
  },

  async updateAgenciesList({ commit, rootGetters, state }) {
    const role = rootGetters["application/getLoggedRole"];
    if (!isAllowedTo([ROLE.ADMIN, ROLE.MANAGER, ROLE.SERVICEDESK], role)) return;

    try {
      const agencies = await this.$services.agency.fetchAllAgencies(state.instanceId, {
        size: 10000,
      });
      commit("SET_AGENCY_LIST", agencies);
    } catch (error) {
      throw new Error(error);
    }
  },

  async fetchAgencies({ commit }, { sectorId, requestParams }) {
    const { page, sortBy, orderBy, filter, size = 10 } = requestParams;

    try {
      const agencies = await this.$services.agency.fetchAgencies(sectorId, {
        page,
        size,
        sortBy,
        orderBy: orderMapper(orderBy),
        filter,
      });
      agencies.sortBy = sortBy;
      agencies.orderBy = orderBy;
      commit("SET_AGENCIES", { parent: sectorId, agencies });
    } catch (error) {
      throw new Error(error);
    }
  },

  removeAgencies({ commit }, sectorId) {
    commit("REMOVE_AGENCIES", { parent: sectorId });
  },

  async setCurrentSector({ commit }, id) {
    try {
      const sector = await this.$services.agency.fetchSector(id);
      commit("SET_CURRENT_SECTOR", toSector(sector));
    } catch (error) {
      throw new Error(error);
    }
  },

  async setCurrentAgency({ commit }, id) {
    try {
      const agency = await this.$services.agency.fetchAgency(id);
      commit("SET_CURRENT_AGENCY", toAgency(agency));
    } catch (error) {
      throw new Error(error);
    }
  },

  async addSector({ commit }, sector) {
    try {
      const created = await this.$services.agency.createSector(sector);
      commit("ADD_SECTOR", toSector(created));
      this.$feedback.ok("feedback.create.sector.ok");
    } catch {
      this.$feedback.error("feedback.create.sector.error");
    }
  },

  async setSector({ commit }, { savedSector, id }) {
    try {
      const updated = await this.$services.agency.updateSector(id, savedSector);
      commit("SET_SECTOR", toSector(updated));
      this.$feedback.ok("feedback.update.sector.ok");
    } catch {
      this.$feedback.error("feedback.update.sector.error");
    }
  },

  async deleteSector({ commit }, id) {
    try {
      await this.$services.agency.deleteSector(id);
      commit("DELETE_SECTOR", id);
      this.$feedback.ok("feedback.delete.sector.ok");
    } catch {
      this.$feedback.error("feedback.delete.sector.error");
    }
  },

  async addAgency({ commit }, agency) {
    try {
      const created = await this.$services.agency.createAgency(agency);
      commit("ADD_AGENCY", toAgency(created));
      this.$feedback.ok("feedback.create.agency.ok");
    } catch (error) {
      const { error: prefix, message } = error.response.data;
      this.$feedback.error(`${prefix}${message}`);
    }
  },

  async setAgency({ commit, dispatch }, { savedAgency, id }) {
    commit("SET_FORM_BACKEND_VALIDATED", false);
    try {
      const updated = await this.$services.agency.updateAgency(id, savedAgency);
      commit("SET_AGENCY", toAgency(updated));
      commit("SET_FORM_BACKEND_VALIDATED", true);
      dispatch("application/updateNavLink", { savedAgency, id }, { root: true });
      this.$feedback.ok("feedback.update.agency.ok");
    } catch (error) {
      if (error.response?.data?.message === "badHierarchy") {
        commit("SET_FORM_BACKEND_VALIDATED", false);
        const { error: prefix, message } = error.response.data;
        this.$feedback.error(`${prefix}${message}`);
      } else {
        this.$feedback.error("feedback.update.agency.error");
      }
    }
  },

  async deleteAgency({ commit }, { agencyId, sectorId }) {
    try {
      await this.$services.agency.deleteAgency(agencyId);
      commit("DELETE_AGENCY", { agencyId, sectorId });
      this.$feedback.ok("feedback.delete.agency.ok");
    } catch (error) {
      if (error.response?.data?.message === "agency.has_active_agents") {
        this.$feedback.error("feedback.delete.agency.hasActiveAgents");
      } else {
        this.$feedback.error("feedback.delete.agency.error");
      }
    }
  },

  async moveSelectedAgencies({ state, dispatch }, payload) {
    const { destination, agencies, sectorId, certId, actOnChildren, actOnUsers } = payload;
    try {
      await this.$services.agency.moveAgencies(state.instanceId, {
        sectorId,
        agencies,
        certId,
        instanceId: destination,
        moveAgenciesChildren: actOnChildren,
        migrateUsers: actOnUsers,
      });
      dispatch("fetch", {});
      this.$feedback.ok("agencies.globalActions.move.ok");
    } catch {
      this.$feedback.error("agencies.globalActions.move.error");
    }
  },

  async updateAllAgents({ state }) {
    try {
      await this.$services.agency.updateAllAgents(state.instanceId);
      this.$feedback.ok("feedback.update.allAgents.ok");
    } catch {
      this.$feedback.error("feedback.update.allAgents.error");
    }
  },

  async updateSelectedAgents({ state }, { selection, actOnChildren }) {
    try {
      await this.$services.agency.updateSelectedAgents(state.instanceId, {
        agencies: selection,
        updateAgenciesChildren: actOnChildren,
      });
      this.$feedback.ok("feedback.update.selectedAgents.ok");
    } catch {
      this.$feedback.error("feedback.update.selectedAgents.error");
    }
  },

  async fetchAgencyVaccineException({ commit }, { agencyId, vaccineId }) {
    try {
      const exception = await this.$services.agency.fetchVaccineException(agencyId, vaccineId);
      commit("SET_EDITING_AGENCY_VACCINE_EXCEPTION", exception);
    } catch (error) {
      throw new Error(error);
    }
  },

  async setAgencyVaccineException(_, { agencyId, vaccineId, savedAgencyVaccineException }) {
    try {
      await this.$services.agency.updateVaccineException(
        agencyId,
        vaccineId,
        savedAgencyVaccineException,
      );
      this.$feedback.ok("feedback.update.agencyException.ok");
    } catch {
      this.$feedback.error("feedback.update.agencyException.error");
    }
  },

  async prepareCsv({ state, dispatch, commit }, requestParams) {
    try {
      await this.$services.agency.prepareCsv(state.instanceId, requestParams);
      await dispatch("application/setDownloads", {}, { root: true });
      commit("application/SET_IS_WAITING_FOR_DOWNLOADS", true, { root: true });
      this.$feedback.ok("generic.download.ok");
    } catch {
      this.$feedback.error("generic.download.error");
    }
  },

  setLoading({ commit }, loading) {
    commit("SET_LOADING", loading);
  },
};
